import { z } from 'zod';
import {
  MeResponseSchema,
  PasswordStatusResponseSchema,
  type MeResponse,
  type PasswordLoginBody,
  type PasswordSetupBody,
  type PasswordStatusResponse,
  type RequestCodeBody,
  type VerifyCodeBody,
} from '../../src/shared/auth';
import { apiGet, apiPost } from './client';

/**
 * Typed client for the auth endpoints (ARCHITECTURE_PLAN §6.1, §9):
 *   POST /api/auth/request-code   — email + Turnstile token → 6-digit OTP email
 *   POST /api/auth/verify-code    — email + code → session cookie (HttpOnly)
 *   POST /api/auth/logout         — destroys the session row
 *   GET  /api/auth/me             — session bootstrap for route guards (§10)
 *
 * Request bodies are typed by the shared schemas; success payloads are
 * validated on arrival. `request-code`/`verify-code`/`logout` only need the
 * success signal — their `data` contracts are pinned in `src/shared` by the
 * backend slice, so they are validated as envelope-only here rather than
 * against an invented local shape (§3 single source of truth).
 */

/** React Query key for the session bootstrap. Single definition, used everywhere. */
export const authKeys = {
  me: ['auth', 'me'] as const,
};

/** `POST /api/auth/request-code` (§6.1 step 1). */
export async function requestCode(body: RequestCodeBody): Promise<void> {
  await apiPost('/api/auth/request-code', body, z.unknown());
}

/** `POST /api/auth/verify-code` (§6.1 step 2). The server sets the session cookie. */
export async function verifyCode(body: VerifyCodeBody): Promise<void> {
  await apiPost('/api/auth/verify-code', body, z.unknown());
}

/** `POST /api/auth/logout`. Idempotent server-side; sends an empty JSON object for the CSRF guard. */
export async function logout(): Promise<void> {
  await apiPost('/api/auth/logout', {}, z.unknown());
}

/** React Query key for the password-setup bootstrap probe. */
export const passwordStatusKey = ['auth', 'password', 'status'] as const;

/** `GET /api/auth/password/status` — is the one-time super-user setup still open? */
export function fetchPasswordStatus(signal?: AbortSignal): Promise<PasswordStatusResponse> {
  return apiGet('/api/auth/password/status', PasswordStatusResponseSchema, signal);
}

/** `POST /api/auth/password/setup` — one-time super-user password claim. Sets the session cookie. */
export async function passwordSetup(body: PasswordSetupBody): Promise<void> {
  await apiPost('/api/auth/password/setup', body, z.unknown());
}

/** `POST /api/auth/password/login` — platform-admin password login. Sets the session cookie. */
export async function passwordLogin(body: PasswordLoginBody): Promise<void> {
  await apiPost('/api/auth/password/login', body, z.unknown());
}

/**
 * `GET /api/auth/me` — the route-guard bootstrap (§10). Returns
 * `{ user: null, memberships: [] }` for a logged-out visitor (NOT an error), so
 * guards branch on `user === null` instead of catching.
 */
export function fetchMe(signal?: AbortSignal): Promise<MeResponse> {
  return apiGet('/api/auth/me', MeResponseSchema, signal);
}
